<script>
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from 'vue-router';
import {DonationTypes, RecurringDonation, useDonationSessionStore} from "@/javascript/stores/donationSession";
import StripePaymentForm from "@/javascript/views/paymentProcessors/StripePaymentForm.vue";
import CurrencyInput from "@/javascript/views/components/currencyInput/currencyInput.vue";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import Tooltip from "../components/tooltip.vue";
import HelcimPaymentForm from "../paymentProcessors/HelcimPaymentButton.vue";
import DonationCheckoutSummary from "./DonationCheckoutSummary.vue";
import {ApplicationHelper} from "../../helpers/ApplicationHelper";
import DonationTipForm from "./DonationTipForm.vue";
import StripePaymentButton from "../paymentProcessors/StripePaymentButton.vue";
import HelcimPaymentButton from "../paymentProcessors/HelcimPaymentButton.vue";
import Popup from "../components/popup.vue";
import DonationCheckoutTotal from "./DonationCheckoutTotal.vue";
import {useLandingPageStore} from "../../stores/landingPage";
import PageHeader from "../components/landing/PageHeader.vue";
import GiftBasketSideBarButton from "../landing/GiftBasketSidebarButton.vue";
import LoadingSpinner from "../components/loadingSpinner.vue";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import SignInModal from "../components/SignInModal.vue";
import {useUserStore} from "../../stores/user";
import BaseModal from "../components/BaseModal.vue";

export default {
  name: "NewDonationPage",

  components: {
    SignInModal,
    LoadingSpinner,
    GiftBasketSideBarButton, PageHeader,
    DonationCheckoutTotal,
    Popup,
    HelcimPaymentButton, StripePaymentButton,
    DonationTipForm,
    DonationCheckoutSummary,
    HelcimPaymentForm,
    Tooltip,
    StripePaymentForm,
    CurrencyInput,
    VueDatePicker,
    BaseModal
  }, // Updated donation page for Helcim popup TODO rename
  props: {
    compact: {
      type: Boolean,
      default: false
    },
    hideTitle: {
      type: Boolean,
      default: false
    },
    /**
     UUID of the donatable, when passed in, will set the donatable as the default of the form if exists.
     **/
    donatableId: {
      type: String,
      require: false
    },
    hideDonationTarget: {
      type: Boolean,
      default: false
    }
  },
  emits: ['payment-success'],
  setup(props) {
    // Stores
    const donationSessionStore = useDonationSessionStore();
    const landingPageStore = useLandingPageStore();
    const analyticEventStore = useAnalyticEventStore();
    const userStore = useUserStore();

    // I18n
    const {t} = useI18n();

    // Router
    const router = useRouter();
    const route = useRoute();

    // This will toggle the loaded state of the payment form
    // We need to update the payment intent if the user changes the amount or type, which requires a relaoad of the form
    // This disallows the user from changing the amount/ type at a bad time during the payment process


    return {
      t,
      donationSessionStore,
      landingPageStore,
      analyticEventStore,
      userStore,
      router,
      route
    };
  },

  data() {
    return {
      loading: true,
      organizationId: null,
      errorPopupMessage: "Something went wrong! Please try again or contact support.",
      customAmount: false,
      showSignInModal: false,
      userSignedIn: false,
      userSignOutClicked: false,
      signingOut: false,
      signedOut: false
    };
  },
  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    DonationTypes() {
      return DonationTypes;
    },
    RecurringDonation() {
      return RecurringDonation;
    },

    formErrors() {
      return this.donationSessionStore.donationForm.errors;
    },
    presetAmounts() {
      const target = this.formData.selectedTarget;
      if (target?.donation_suggestions_enabled && target?.donation_suggestions?.length > 0 ) {
        return target.donation_suggestions;
      }
      return [];
    },
    formData() {
      return this.donationSessionStore.donationForm;
    },
    paymentFormLoaded() {
      return this.donationSessionStore.donationSession.loaded;
    },
    currentOrganization() {
      return this.donationSessionStore.currentOrganization;
    },
    showTargetDropdown() {
      return this.donationSessionStore.showDonationTargetDropdown && !this.hideDonationTarget;
    },
    showDonationTypeDropdown() {
      return this.donationSessionStore.orgProcessor
        && this.donationSessionStore.orgProcessor.subscriptions_enabled;
    },
    showDelayedSubscriptionToggle() {
      return this.donationSessionStore.orgProcessor
        && this.donationSessionStore.orgProcessor.delayed_subscriptions_enabled;
    },
    showHelcimPaymentButton() {
      return this.donationSessionStore.orgProcessorType?.includes("Helcim");
    },
    showStripePaymentButton() {
      return this.donationSessionStore.orgProcessorType?.includes("Stripe");
    },
    cssProps() {
      return {
        '--button-color': this.landingPageStore.localBrand.color_primary,
        '--button-hover': this.landingPageStore.localBrand.color_primary_dark,
        '--button-text': this.landingPageStore.localBrand.color_primary_text,
        '--input-focus': this.landingPageStore.localBrand.color_primary,
      };
    },

    // Determine the max date to display in delay donation date picker, based on the selected frequency
    // Max of 3 months delay for  quarterly and yearly donations
    calendarMax() {
      const frequency = this.donationSessionStore.donationForm.frequency;
      let today = new Date();
      let futureDate = new Date();

      switch (frequency.toLowerCase()) {
        case "weekly":
          futureDate.setDate(today.getDate() + 8);
          break;
        case "biweekly":
          futureDate.setDate(today.getDate() + 15);
          break;
        case "monthly":
          futureDate.setMonth(today.getMonth() + 1);
          break;
        case "quarterly":
          futureDate.setMonth(today.getMonth() + 3);
          break;
        case "yearly":
          futureDate.setMonth(today.getMonth() + 3);
          break;
      }

      return futureDate;
    },

    // Date must be in the future for delayed donations
    calendarMin() {
      let today = new Date();
      let tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);
      return tomorrow;
    }
  },

  mounted() {
    // Set org id from route if present
    // Otherwise landing page store endpoint will get it from subdomain on the backend
    this.organizationId = this.route.params.org;
    if(this.organizationId) {
      this.landingPageStore.setOrganizationId(this.organizationId);
    }

    // Loads the branding and other data
    // Grabs the organization from subdomain if needed
    // TODO this only works if there's a published page, write a new endpoint
    this.landingPageStore.loadPublishedPage().then(() => {
      if (!this.organizationId) {
        this.organizationId = this.landingPageStore.organizationId;
      }
      this.analyticEventStore.setOrganizationId(this.organizationId);
      this.analyticEventStore.logEvent({ action: 'giving-form-visit' });

      // Check if the URL contains source=qr query parameter
      if (this.route.query.source === 'qr') {
        this.analyticEventStore.logEvent({
          action: 'givingform-qr-visit',
          metadata: {
            timestamp: new Date().toISOString(),
            destination: window.location.href,
            source: 'QR Code',
          }
        });
      }

      if (this.donatableId) {
        this.donationSessionStore.setDefaultTarget(this.donatableId);
      }

      this.donationSessionStore.loadOrgDonationForm(this.organizationId)
        .then(() => {
          this.loading = false;
          if (!this.donationSessionStore.currentOrganization) {
            this.router.push({path: '/error'});
          }
          // Check if user is already signed in (e.g. from landing page or SSO redirect)
          const currentUserId = document.body.dataset.currentUserId;
          if (currentUserId) {
            this.userStore.userId = currentUserId;
            this.userStore.getUser().then(() => {
              if (this.userStore.user) {
                this.formData.fName = this.userStore.user.first_name || '';
                this.formData.lName = this.userStore.user.last_name || '';
                this.formData.donorEmail = this.userStore.user.email || '';
                this.userSignedIn = true;
              }
            });
          }
        });
    });
  },
  methods: {

    selectPresetAmount(amount) {
      this.formData.amount = amount;
      this.customAmount = false;
    },
    selectCustomAmount() {
      this.customAmount = true;
      this.formData.amount = null;
    },
    togglePaymentForm() {
      this.analyticEventStore.logEvent({
        action: `giving-page-toggle-payment-form`,
        traceable_type: this.donationSessionStore.donationForm.selectedTarget.model,
        traceable_id: this.donationSessionStore.donationForm.selectedTarget.id,
        metadata: {
          email: this.donationSessionStore.donationForm.donorEmail,
          f_name: this.donationSessionStore.donationForm.fName,
          l_name: this.donationSessionStore.donationForm.lName,
          amount: this.donationSessionStore.donationForm.amount,
          donation_type: this.donationSessionStore.donationForm.donationType,
        }
      });
      this.donationSessionStore.togglePaymentForm();

      // Scroll after the form renders
      this.$nextTick(() => {
        if (window.innerWidth >= 768) {
          this.$refs.donationPageContainer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          this.$refs.paymentFormContainer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    showErrorPopup(message) {
      if (message && message.includes("payment_processor_unavailable")) {
        this.errorPopupMessage = "The payment processor is currently unavailable. Please try again later or contact the organization.";
      } else {
        this.errorPopupMessage = message || "Something went wrong! Please try again or contact support.";
      }
      this.$refs.errorPopup.show();
    },

    donateButtonClicked() {
      if (this.donationSessionStore.donationFormValid(false)) {
        this.$refs.paymentButton.initPaymentForm();
      }
    },

    logoClicked() {
      ApplicationHelper.redirectToExternalUrl(this.currentOrganization.website_url);
    },

    onUserSignedIn(userData){
      this.userSignedIn = true;
      this.formData.fName = userData.fName || '';
      this.formData.lName = userData.lName || '';
      this.formData.donorEmail = userData.donorEmail || '';
    },

    openSignOutModal() {
      this.userSignOutClicked = true;
    },

    signOutUser() {
      this.signingOut = true;
      this.userStore.signOut().then(() => {
        this.userSignedIn = false;
        this.donationSessionStore.resetState();
        this.signedOut = true;
        setTimeout(() => this.closeSignOutModal(), 1500);
        this.analyticEventStore.logEvent({
          action: `checkout-flow-sign-out`
        });
      });

    },

    closeSignOutModal() {
      this.userSignOutClicked = false;
      this.signingOut = false;
      this.signedOut = false;
    }
  },
};
</script>

<template>
  <div
    ref="donationPageContainer"
    class="donation-page-container"
    :style="cssProps"
  >
    <div
      v-if="loading"
      class="inset-0 flex items-center justify-center fixed"
    >
      <loading-spinner size="small" />
    </div>

    <div v-else>
      <!-- Page Header -->
      <div class="relative z-10">
        <page-header
          :org-name="currentOrganization?.name"
          :logo-resource="landingPageStore.localBrand?.logo"
          :show-logo="true"
          :donate-button-background-color="landingPageStore.localBrand.color_primary"
          :donate-button-hover-background-color="landingPageStore.localBrand.color_primary_dark"
          :disable-navigation="true"
          @logo-click="logoClicked"
        />
      </div>

      <!-- Main Layout -->
      <div class="flex flex-col md:flex-row min-w-full md:min-h-screen">
        <!-- Form Card -->
        <div
          class="form-card"
          :class="{ 'payment-form-loaded': paymentFormLoaded }"
        >
          <!-- Header -->
          <div class="form-header">
            <div class="flex items-center gap-4 order-first md:order-last self-end md:self-auto">
              <div v-if="!userSignedIn">
                <span class="text-sm text-gray-600">Already have an account? </span>
                <span
                  class="text-sm underline cursor-pointer text-gray-600 hover:text-gray-800"
                  @click="showSignInModal = true"
                >
                  Sign In
                </span>
              </div>
              <div v-else-if="userSignedIn">
                <span
                  class="text-sm underline cursor-pointer text-gray-600 hover:text-gray-800"
                  @click="openSignOutModal"
                >
                  Sign Out
                </span>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <i class="fa-solid fa-gift header-icon" />
              <div>
                <h1 class="form-title">
                  Make a Difference
                </h1>
                <p class="form-subtitle">
                  Your gift helps {{ currentOrganization?.name }} continue our mission.
                </p>
              </div>
            </div>
          </div>

          <!-- Gift Amount Section -->
          <div class="form-section">
            <div class="flex flex-col md:flex-row gap-6">
              <div class="flex-1">
                <label class="form-label">Gift Amount</label>
                <p class="form-hint">
                  Enter the amount you'd like to give.
                </p>
                <currency-input
                  v-model="formData.amount"
                  prefix="$ "
                  class="max-h-12"
                  :max="10000"
                  :disabled="paymentFormLoaded"
                />
                <span
                  v-if="formErrors.amount"
                  class="form-error"
                >{{ formErrors.amount }}</span>

                <!-- Preset Chips -->
                <div class="preset-chips">
                  <button
                    v-for="amount in presetAmounts"
                    :key="amount"
                    type="button"
                    class="chip"
                    :class="{ active: formData.amount === amount && !customAmount }"
                    :disabled="paymentFormLoaded"
                    @click="selectPresetAmount(amount)"
                  >
                    ${{ amount }}
                  </button>
                  <!--                <button-->
                  <!--                  type="button"-->
                  <!--                  class="chip"-->
                  <!--                  :class="{ active: customAmount }"-->
                  <!--                  :disabled="paymentFormLoaded"-->
                  <!--                  @click="selectCustomAmount"-->
                  <!--                >-->
                  <!--                  Other-->
                  <!--                </button>-->
                </div>
              </div>

              <div
                v-if="showTargetDropdown"
                class="flex-1"
              >
                <div class="flex items-center gap-2">
                  <label class="form-label">Designation</label>
                </div>
                <p class="form-hint">
                  Where would you like your gift to go?
                </p>
                <select
                  v-model="formData.selectedTarget"
                  :disabled="paymentFormLoaded"
                  class="w-full"
                >
                  <option
                    v-for="(value, key) in formData.donationTargets"
                    :key="key"
                    :value="value"
                  >
                    {{ value.name }}
                  </option>
                </select>
                <span
                  v-if="formErrors.target"
                  class="form-error"
                >{{ formErrors.target }}</span>
              </div>
            </div>

            <!-- One Time / Recurring Toggle -->
            <div
              v-if="showDonationTypeDropdown"
              class="mt-6"
            >
              <label class="form-label">One Time or Recurring Gift</label>
              <div class="donation-type-toggle">
                <button
                  v-for="(value, key) in DonationTypes"
                  :key="key"
                  type="button"
                  class="toggle-btn"
                  :class="{ active: formData.donationType === value }"
                  :disabled="paymentFormLoaded"
                  @click="formData.donationType = value"
                >
                  {{ t(key) }}
                  <i
                    v-if="formData.donationType === value"
                    class="fa-solid fa-circle-check toggle-check"
                  />
                </button>
              </div>
            </div>

            <!-- Frequency (if recurring) -->
            <div
              v-if="formData.donationType === RecurringDonation"
              class="mt-4"
            >
              <label class="form-label">{{ t('donation.donation_type_frequency') }}</label>
              <select
                v-model="donationSessionStore.donationForm.frequency"
                :disabled="paymentFormLoaded"
                class="w-full"
              >
                <option
                  v-for="(value, key) in donationSessionStore.donationForm.frequencyOptions"
                  :key="key"
                  :value="value"
                >
                  {{ t('donation.' + value) }}
                </option>
              </select>
            </div>
          </div>

          <hr class="section-divider">

          <!-- Your Information Section -->
          <div class="form-section">
            <div class="flex items-center gap-3 mb-4">
              <i class="fa-solid fa-user header-icon" />
              <div>
                <h2 class="form-section-title">
                  Your Information
                </h2>
                <p class="form-hint">
                  We'll use this to send your receipt.
                </p>
              </div>
            </div>

            <div class="flex flex-col md:flex-row gap-4 mb-4">
              <div class="flex-1">
                <label class="form-label">First Name</label>
                <input
                  v-model="formData.fName"
                  placeholder="John"
                  :disabled="paymentFormLoaded"
                >
                <span
                  v-if="formErrors.fName"
                  class="form-error"
                >{{ formErrors.fName }}</span>
              </div>
              <div class="flex-1">
                <label class="form-label">Last Name</label>
                <input
                  v-model="formData.lName"
                  placeholder="Smith"
                  :disabled="paymentFormLoaded"
                >
                <span
                  v-if="formErrors.lName"
                  class="form-error"
                >{{ formErrors.lName }}</span>
              </div>
            </div>

            <div class="mb-4">
              <label class="form-label">Email Address</label>
              <input
                v-model="formData.donorEmail"
                type="email"
                placeholder="name@example.com"
                :disabled="paymentFormLoaded"
              >
              <span
                v-if="formErrors.email"
                class="form-error"
              >{{ formErrors.email }}</span>
              <p class="form-hint mt-1">
                Receipt will be sent here.
              </p>
            </div>
          </div>

          <!-- Review Button -->
          <button
            class="review-btn"
            @click="togglePaymentForm"
          >
            {{ paymentFormLoaded ? t('donation.actions.change') : 'Review Gift' }}
            <i
              v-if="!paymentFormLoaded"
              class="fa-solid fa-arrow-right ml-2 review-arrow"
            />
          </button>

          <!-- Footer -->
          <div class="form-footer">
            <i class="fa-regular fa-heart" />
            <span>Your generosity makes a real difference. Thank you.</span>
          </div>
        </div>

        <!-- Payment Side Panel -->
        <div
          ref="paymentFormContainer"
          :class="{ 'loaded': paymentFormLoaded, 'compact': compact }"
          class="payment-form-container w-full bg-white transition-all duration-500"
        >
          <donation-checkout-summary :remove-from-cart-enabled="false" />
          <donation-tip-form />
          <div class="mt-2">
            <donation-checkout-total />
          </div>
          <div class="mb-12">
            <HelcimPaymentButton
              v-if="showHelcimPaymentButton"
              ref="paymentButton"
              :user-signed-in="userSignedIn"
              :user-data="userSignedIn ? userStore.user : null"
              @click="donateButtonClicked"
              @payment-success="onPaymentSuccess"
              @payment-error="showErrorPopup"
            />
            <StripePaymentButton
              v-if="showStripePaymentButton"
              ref="paymentButton"
              @click="donateButtonClicked"
              @payment-success="onPaymentSuccess"
              @payment-error="showErrorPopup"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <SignInModal
    v-model="showSignInModal"
    @user-signed-in="onUserSignedIn"
  />

  <base-modal
    v-if="userSignOutClicked"
    title="Sign Out?"
    :background-dismissible="false"
    @close="closeSignOutModal"
  >
    <!--  Sign Out pop up confirmation-->
    <template #default>
      <!-- Show success message -->
      <div
        v-if="signedOut"
        class="m-20 text-blue-500 text-xl text-center"
      >
        Signing out!
        <LoadingSpinner size="small mt-2" />
      </div>

      <div v-else>
        <p>Are you sure you want to sign out?</p>
        <div class="mt-4 flex justify-end gap-2">
          <button
            class="signout-btn px-4 py-2 rounded-lg font-semibold text-sm"
            :style="{ backgroundColor: landingPageStore.localBrand?.color_primary, color: landingPageStore.getContrastTextColor(landingPageStore.localBrand?.color_primary) }"
            @click="signOutUser"
          >
            Sign Out
          </button>
          <button
            class="cancel-btn px-4 py-2 rounded-lg font-semibold text-sm border border-black"
            :style="{ '--hover-bg': landingPageStore.localBrand?.color_primary, '--hover-text': landingPageStore.getContrastTextColor(landingPageStore.localBrand?.color_primary) }"
            @click="closeSignOutModal"
          >
            Cancel
          </button>
        </div>
      </div>
    </template>
  </base-modal>
  <popup
    ref="errorPopup"
    :message="errorPopupMessage"
    type="error"
    :duration="7000"
  />
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.donation-page-container {
  @apply min-h-screen bg-gray-50;
  font-family: 'Montserrat', sans-serif;

  .outer-container {
    width: 100%;
    display: flex;
    justify-content: center;

    :deep(.inner-container) {
      width: 100%;
      max-width: 1000px;
      margin: 0 auto;
      padding: 0.5rem 1.5rem;
      display: flex;
      align-items: center;
      min-height: 80px;
    }
  }
}

.form-card {
  @apply bg-white shadow-lg mx-auto p-6 md:p-10;
  @apply flex flex-col w-full transition-all duration-500 ease-in-out drop-shadow-lg;
  max-width: 1000px;
  overflow: hidden;

  @screen md {
    &:not(.payment-form-loaded) {
      @apply w-full;
    }

    &.payment-form-loaded {
      @apply w-1/2;
      margin: 0;
      max-width: none;
    }
  }
}

.form-header {
  @apply flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4;
}

.header-icon {
  font-size: 1.5rem;
  color: var(--button-color);
  background-color: color-mix(in srgb, var(--button-color) 10%, white);
  padding: 10px;
  border-radius: 12px;
}

.form-title {
  @apply text-2xl  text-gray-900;
  font-weight: $section-title-weight;
  margin: 0;
}

.form-subtitle {
  @apply text-sm text-gray-500 mt-1;
  margin-bottom: 0;
}

.secure-badge {
  @apply flex items-center gap-2 text-xs text-gray-400 whitespace-nowrap;
}

.form-section {
  @apply mb-6;
}

.form-label {
  @apply block text-sm font-semibold text-gray-800 mb-1;
}

.form-hint {
  @apply text-xs text-gray-400 mb-2;
}

.form-section-title {
  @apply text-xl text-gray-900;
  font-weight: $section-title-weight;
  margin: 0;
}

.section-divider {
  @apply border-gray-200 my-6;
}

// Preset amount chips
.preset-chips {
  @apply flex flex-wrap gap-2 mt-3;

  .chip {
    @apply px-4 py-2 text-sm font-medium rounded-full border border-gray-300 bg-white text-gray-700;
    width: auto;
    min-width: auto;

    &:hover:not(:disabled) {
      border-color: var(--button-color);
      color: var(--button-color);
    }

    &.active {
      border-color: var(--button-color);
      background-color: color-mix(in srgb, var(--button-color) 10%, white);
      color: var(--button-color);
    }

    &:disabled {
      @apply opacity-50 cursor-not-allowed;
    }
  }
}

// Donation type toggle
.donation-type-toggle {
  @apply flex gap-3 mt-2;

  .toggle-btn {
    @apply flex-1 py-3 px-4 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700;
    @apply flex items-center justify-center gap-2;
    width: auto;

    &:hover:not(:disabled) {
      border-color: var(--button-color);
    }

    &.active {
      border-color: var(--button-color);
      background-color: color-mix(in srgb, var(--button-color) 10%, white);
      color: var(--button-color);
    }

    .toggle-check {
      color: var(--button-color);
    }
  }
}

// Inputs
input, :deep(input), select {
  @apply bg-white border border-gray-300 disabled:bg-gray-50;
  @apply w-full px-4 py-3 text-base leading-normal rounded-lg focus:outline-none max-h-12;

  &:focus {
    border: 1px solid var(--input-focus);
  }
}

// Review button
.review-btn {
  @apply w-full py-3 px-4 rounded-lg text-base font-semibold mt-4;
  background-color: var(--button-color);
  color: var(--button-text);

  &:hover {
    background-color: var(--button-hover);
  }

  .review-arrow {
    @apply inline-block;

    //Arrow pointing down since that where the rest of the form will appear
    @media screen and (max-width: 768px) {
      transform: rotate(90deg);
    }
  }
}

// Reset generic button styles inside form card so chips/toggles aren't affected
.form-card button:not(.review-btn) {
  background-color: transparent;
  color: inherit;
}

.form-error {
  @apply text-red-500 text-xs mt-1;
}

.form-footer {
  @apply flex items-center justify-center gap-2 text-sm text-gray-400 mt-8;
}

// Payment side panel
.payment-form-container {
  @apply transition-all duration-500 ease-in-out;

  &:not(.loaded) {
    @apply md:w-0 md:h-0 hidden;
  }

  &.loaded {
    @apply md:w-1/2;
    @apply px-4 md:px-8;

    &.compact {
      padding: 20px 0 !important;
      @screen md {
        padding: 0 20px !important;
      }
    }
  }
}

.cancel-btn {
  background-color: transparent !important;
  color: black !important;
  border-color: black !important;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--hover-bg) !important;
    color: var(--hover-text) !important;
    border-color: var(--hover-bg) !important;
  }
}

.signout-btn {
  transition: all 0.2s ease;
  border: 2px solid transparent !important;

  &:hover {
    border-color: black !important;
  }
}

// Mobile
@media screen and (max-width: 768px) {
  .form-card {
    @apply rounded-none shadow-none mx-0;
  }

  .preset-chips .chip {
    @apply flex-1 text-center;
    min-width: calc(33% - 0.5rem);
  }

  .donation-type-toggle {
    @apply flex-col;
  }

  .payment-panel {
    @apply rounded-none shadow-none mx-0;
  }
}
</style>
